import { Avatar, Body, Heading, Icon } from '@assembly-js/design-system';
import {
  Button,
  MenuItem,
  MenuList,
  Popover,
  Tooltip,
  useTheme,
} from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import React from 'react';
import { shallowEqual } from 'react-redux';

import { NotificationBadge } from 'src/components/NotificationBadge';
import { Company } from 'src/constants';
import { PortalConfigContext, SidebarTheme } from 'src/context';
import { InboxNotificationDetailsResponse } from 'src/entities/Notifications';
import { useCompanySwitcherVisibility } from 'src/features/companies/useCompanySwitcherVisibility';
import history from 'src/history';
import { useLogout } from 'src/hooks/useLogout';
import { useNotificationBadgeData } from 'src/hooks/useNotificationBadgeData';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { ApiTags } from 'src/services/api/appApi';
import {
  applicationSettingsApi,
  useGetClientCompanyVisibilityQuery,
} from 'src/services/api/applicationsApi';
import { contractsApi } from 'src/services/api/contractsApi';
import { formsApi } from 'src/services/api/formsApi';
import { useGetInboxNotificationsQuery } from 'src/services/api/inboxApi';
import { invoicesApi } from 'src/services/api/invoicesApi';
import { usersApi } from 'src/services/api/usersApi';
import { setActiveCompanyId } from 'src/store/activeCompany/slice';
import { selectAllCompaniesMap } from 'src/store/clients/types';
import { listArticles } from 'src/store/knowledgeBase/actions';
import { RootState } from 'src/store/reduxTypes';
import { selectCurrentUser } from 'src/store/storeUtils';
import { isUnreadNotification } from 'src/utils/NotificationsUtils';

// Maps each built-in notification slice to the module id whose per-company
// visibility gates it (note invoices live under the "payments" module).
const BUILT_IN_NOTIFICATION_MODULE_IDS = {
  files: 'files',
  invoices: 'payments',
  forms: 'forms',
  contracts: 'contracts',
} as const;

// Module id for the Messages app, whose unread count is gated the same way.
export const MESSAGES_MODULE_ID = 'messages';

// The module ids and custom-app ids a client can see within a single company.
interface CompanyVisibility {
  moduleIds: Set<string>;
  appIds: Set<string>;
}

export type CompanyVisibilityMap = Map<string, CompanyVisibility>;

const selectCompanyUnreadNotifications = createSelector(
  [
    (state: RootState) => state.notifications.fileNotifications,
    (state: RootState) => state.notifications.newInvoiceNotifications,
    (state: RootState) => state.notifications.formRequestNotifications,
    (state: RootState) => state.notifications.esigRequestNotifications,
    (
      _state: RootState,
      inboxNotifications: InboxNotificationDetailsResponse[] | undefined,
    ) => inboxNotifications,
    (
      _state: RootState,
      _inboxNotifications: InboxNotificationDetailsResponse[] | undefined,
      companyVisibility: CompanyVisibilityMap,
    ) => companyVisibility,
  ],

  (
    fileNotifications,
    newInvoiceNotifications,
    formRequestNotifications,
    esigRequestNotifications,
    inboxNotifications,
    companyVisibility,
  ) => {
    const companyToUnreadCount = new Map<string, number>();

    const increment = (companyId: string) => {
      companyToUnreadCount.set(
        companyId,
        (companyToUnreadCount.get(companyId) || 0) + 1,
      );
    };

    // Visibility is resolved per company. Until a company's visibility is known
    // we don't count its notifications, so the badge only ever rises to the
    // correct value rather than showing an inflated count that then drops.
    const isModuleVisible = (companyId: string, moduleId: string) => {
      const visibility = companyVisibility.get(companyId);
      return visibility ? visibility.moduleIds.has(moduleId) : false;
    };
    const isAppVisible = (companyId: string, appId: string) => {
      const visibility = companyVisibility.get(companyId);
      return visibility ? visibility.appIds.has(appId) : false;
    };

    // Built-in module notifications, each gated by whether its module is
    // visible in the company the notification belongs to.
    const countBuiltIn = (
      moduleId: string,
      notifications: Array<{ companyId?: string; ownerId: string }>,
    ) => {
      for (const notification of notifications) {
        const { companyId, ownerId } = notification;
        if (!companyId || !isUnreadNotification(ownerId)) continue;
        if (!isModuleVisible(companyId, moduleId)) continue;
        increment(companyId);
      }
    };
    countBuiltIn(BUILT_IN_NOTIFICATION_MODULE_IDS.files, fileNotifications);
    countBuiltIn(
      BUILT_IN_NOTIFICATION_MODULE_IDS.invoices,
      newInvoiceNotifications,
    );
    countBuiltIn(
      BUILT_IN_NOTIFICATION_MODULE_IDS.forms,
      formRequestNotifications,
    );
    countBuiltIn(
      BUILT_IN_NOTIFICATION_MODULE_IDS.contracts,
      esigRequestNotifications,
    );

    // Custom app / embed notifications, gated by whether the app is visible in
    // the company the notification belongs to.
    for (const notification of inboxNotifications ?? []) {
      if (notification.event !== 'customApp.action' || notification.isRead) {
        continue;
      }
      const { companyId, appId } = notification;
      if (!companyId || !isAppVisible(companyId, appId)) continue;
      increment(companyId);
    }

    return companyToUnreadCount;
  },
);

function getCompanyInitials(name: string) {
  return name
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase())
    .join('')
    .slice(0, 2);
}

export const useCompanySwitcher = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { SignoutRedirectURL: signoutRedirectURL } =
    React.useContext(PortalConfigContext);
  const companySwitcherVisibility = useCompanySwitcherVisibility();

  const { activeClientCompanyId, isClient } = useAppSelector(
    (state) => ({
      activeClientCompanyId: state.activeCompany.activeCompanyId,
      isClient: state.user.isClient,
    }),
    shallowEqual,
  );

  // Get current user and companies map from Redux store
  const currentUser = useAppSelector(selectCurrentUser);
  const companiesMap = useAppSelector(selectAllCompaniesMap);

  // Get user companies using efficient map lookup
  const userCompanies = React.useMemo(() => {
    if (!currentUser || !isClient || !currentUser.fields.companyIds) {
      return [];
    }

    return currentUser.fields.companyIds
      .map((companyId) => companiesMap.get(companyId))
      .filter((company): company is Company =>
        Boolean(company && company.fields.name),
      )
      .sort((a, b) => {
        const nameA = a.fields.name.trim().toLowerCase();
        const nameB = b.fields.name.trim().toLowerCase();
        const nameComparison = nameA.localeCompare(nameB);

        // If names are the same, use company ID for deterministic ordering
        if (nameComparison === 0) {
          return a.id.localeCompare(b.id);
        }

        return nameComparison;
      });
  }, [currentUser, isClient, companiesMap]);

  // Get inbox notifications data
  const { data: inboxNotifications } = useGetInboxNotificationsQuery();

  // App visibility is per-company, so resolve the visible apps for each of the
  // client's companies and gate each notification by the company it belongs to.
  // This keeps a non-active company's badge correct without having to switch to
  // it (the active company's installs alone can't describe the others).
  const companyIds = React.useMemo(() => {
    const ids = currentUser?.fields.companyIds ?? [];
    // Sorted for a stable query cache key regardless of source ordering.
    return [...ids].sort();
  }, [currentUser]);

  const { data: clientCompanyVisibility } = useGetClientCompanyVisibilityQuery(
    companyIds,
    { skip: !isClient || companyIds.length === 0 },
  );

  // companyId -> the module ids and custom-app ids visible in that company.
  const companyVisibility = React.useMemo(() => {
    const map: CompanyVisibilityMap = new Map();
    for (const group of clientCompanyVisibility ?? []) {
      map.set(group.companyId, {
        moduleIds: new Set(group.moduleIds),
        appIds: new Set(group.appIds),
      });
    }
    return map;
  }, [clientCompanyVisibility]);

  const companyToUnreadNotifications = useAppSelector((state) =>
    selectCompanyUnreadNotifications(
      state,
      inboxNotifications,
      companyVisibility,
    ),
  );

  const companyToUnreadMessages = useAppSelector(
    (state) => state.messages.totalUnreadMessagesByCompany,
  );

  const getCompanyUnreadNotificationsCount = React.useCallback(
    (companyId: string) => {
      const unreadAppsNotifications =
        companyToUnreadNotifications.get(companyId) ?? 0;
      // Messages are gated by the Messages app's per-company visibility too;
      // count them only where Messages is visible to the client.
      const messagesVisible = Boolean(
        companyVisibility.get(companyId)?.moduleIds.has(MESSAGES_MODULE_ID),
      );
      const unreadMessages = messagesVisible
        ? (companyToUnreadMessages?.[companyId] ?? 0)
        : 0;

      return unreadAppsNotifications + unreadMessages;
    },
    [companyToUnreadNotifications, companyToUnreadMessages, companyVisibility],
  );

  const handleLogout = useLogout();
  const handleClick = (company: Company) => () => {
    dispatch(setActiveCompanyId(company.id));
    history.push('/');
  };

  React.useEffect(() => {
    if (userCompanies.length > 0) {
      const localActiveCompanyId = localStorage.getItem('activeCompanyId');
      const isInvalidLocalActiveCompanyId =
        localActiveCompanyId &&
        !userCompanies.some((c) => c.id === localActiveCompanyId);
      if (isInvalidLocalActiveCompanyId) {
        // This block handles various scenarios that result in the localStorage
        // value being omitted from the user's companies list:
        // 1. Internal users copying magic links and signing in as many clients.
        // 2. Clients being removed from a company between sessions.
        localStorage.removeItem('activeCompanyId');
        dispatch(setActiveCompanyId(userCompanies.at(0)?.id ?? ''));
      } else if (!activeClientCompanyId) {
        const activeCompanyId =
          localActiveCompanyId ?? userCompanies.at(0)?.id ?? '';
        dispatch(setActiveCompanyId(activeCompanyId));
      }
    }
  }, [userCompanies, activeClientCompanyId]);

  React.useEffect(() => {
    if (!isClient) {
      return;
    }
    if (activeClientCompanyId) {
      localStorage.setItem('activeCompanyId', activeClientCompanyId);
    }
  }, [activeClientCompanyId, isClient]);

  React.useEffect(() => {
    dispatch(applicationSettingsApi.util.invalidateTags([ApiTags.apps]));
    dispatch(usersApi.util.invalidateTags([ApiTags.users]));
    dispatch(contractsApi.util.invalidateTags([ApiTags.contracts]));
    dispatch(invoicesApi.util.invalidateTags([ApiTags.invoices]));
    dispatch(formsApi.util.invalidateTags([ApiTags.form_responses]));
    dispatch(listArticles());
    dispatch(usersApi.util.invalidateTags([ApiTags.otherUserData]));
  }, [dispatch, activeClientCompanyId]);

  const selectedCompany = React.useMemo(() => {
    return userCompanies?.find(
      (company) => company.id === activeClientCompanyId,
    );
  }, [userCompanies, activeClientCompanyId]);

  return {
    companies: userCompanies,
    companySwitcherVisibility,
    handleClick,
    handleLogout,
    theme,
    selectedCompany,
    signoutRedirectURL,
    getCompanyUnreadNotificationsCount,
  } as const;
};

export const DesktopCompanySwitcher = ({ theme }: { theme: SidebarTheme }) => {
  const {
    companies,
    companySwitcherVisibility,
    handleClick,
    selectedCompany,
    getCompanyUnreadNotificationsCount,
  } = useCompanySwitcher();
  const { backgroundColor, textColor } = useNotificationBadgeData(true); // isSidebar = true
  const [hoverCompanyId, setHoverCompanyId] = React.useState<string | null>(
    null,
  );
  const [clampedCompanies, setClampedCompanies] = React.useState<Set<string>>(
    new Set(),
  );
  const textRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());

  // Check for text clamping
  React.useEffect(() => {
    const checkClamping = () => {
      const newClampedCompanies = new Set<string>();

      // Clear stale refs first
      const currentCompanyIds = new Set(companies.map((c) => c.id));
      textRefs.current.forEach((_, companyId) => {
        if (!currentCompanyIds.has(companyId)) {
          textRefs.current.delete(companyId);
        }
      });

      textRefs.current.forEach((element, companyId) => {
        if (element) {
          // Get the Body component's rendered element (should be a <p> tag)
          const bodyElement =
            element.querySelector('p') || element.firstElementChild;

          if (bodyElement && bodyElement instanceof HTMLElement) {
            const { scrollHeight } = bodyElement;
            const { offsetHeight } = bodyElement;
            const isClamped = scrollHeight > offsetHeight;

            if (isClamped) {
              newClampedCompanies.add(companyId);
            }
          }
        }
      });

      setClampedCompanies(newClampedCompanies);
    };

    // Use window resize event instead of ResizeObserver
    const handleResize = () => checkClamping();

    // Check immediately
    checkClamping();

    // Listen for window resize events
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [companies]);

  const setTextRef = React.useCallback(
    (companyId: string) => (element: HTMLDivElement | null) => {
      if (element) {
        textRefs.current.set(companyId, element);
      } else {
        textRefs.current.delete(companyId);
      }
    },
    [],
  );

  const getAvatarStyle = React.useCallback(
    (c: Company) => {
      const isSelected = selectedCompany?.id === c.id;
      const isHovered = hoverCompanyId === c.id;
      return {
        border: 'none',
        marginBottom: -12,
        outline: '2px solid',
        outlineColor: isSelected
          ? theme.sidebarTextColor
          : isHovered
            ? 'gray'
            : 'transparent',
        outlineOffset: 2,
      };
    },
    [selectedCompany, hoverCompanyId, theme],
  );

  if (companySwitcherVisibility.visibility !== 'desktop') {
    return null;
  }

  return (
    <div className="flex flex-col items-center gap-4 py-3 overflow-y-auto h-full">
      {companies.map((company) => {
        const isTextClamped = clampedCompanies.has(company.id);
        const buttonContent = (
          <button
            className="bg-transparent border-none shadow-none cursor-pointer relative flex flex-col items-center gap-5 w-[60px]"
            onClick={handleClick(company)}
          >
            <div className="relative">
              <Avatar
                alt={`${company.fields.name} logo`}
                onMouseOver={() => setHoverCompanyId(company.id)}
                onMouseLeave={() => setHoverCompanyId(null)}
                style={getAvatarStyle(company)}
                fallbackColor={company.fields.fallbackColor}
                size="md"
                src={company.fields.avatarImageURL || ''}
                text={getCompanyInitials(company.fields.name)}
                variant="rounded"
              />

              {getCompanyUnreadNotificationsCount(company.id) > 0 ? (
                <div className="absolute top-[-5px] right-[-5px]">
                  <NotificationBadge
                    showZero={false}
                    isSidebar
                    content={getCompanyUnreadNotificationsCount(company.id)}
                    backgroundColor={backgroundColor}
                    textColor={textColor}
                  />
                </div>
              ) : null}
            </div>
            <div
              className="flex flex-1"
              ref={setTextRef(company.id)}
              style={{ color: theme.sidebarTextColor }}
            >
              <Body className="text-[10px] line-clamp-2 text-center ml-[-8px] mr-[-8px] flex-1">
                {company.fields.name}
              </Body>
            </div>
          </button>
        );

        return isTextClamped ? (
          <Tooltip
            key={company.id}
            title={company.fields.name}
            placement="right"
            enterDelay={300}
          >
            {buttonContent}
          </Tooltip>
        ) : (
          <div key={company.id}>{buttonContent}</div>
        );
      })}
    </div>
  );
};

export const MobileCompanySwitcher = () => {
  const {
    companies,
    companySwitcherVisibility,
    handleClick,
    handleLogout,
    theme,
    selectedCompany,
    signoutRedirectURL,
    getCompanyUnreadNotificationsCount,
  } = useCompanySwitcher();
  const { backgroundColor, textColor } = useNotificationBadgeData(true); // isSidebar = true
  const [open, setOpen] = React.useState(false);
  const anchorEl = React.useRef<HTMLButtonElement>(null);

  if (companySwitcherVisibility.visibility !== 'mobile') {
    return null;
  }

  return (
    <div>
      <Button
        className="bg-transparent border-none shadow-none w-full flex items-center justify-start px-2 py-2"
        ref={anchorEl}
        onClick={() => setOpen(true)}
        style={{
          // TODO: remove this once we integrate the theme in tailwind
          backgroundColor: theme.palette.primary.dark,
        }}
      >
        {selectedCompany && (
          <>
            <Avatar
              className="mr-2 block border-none"
              src={selectedCompany?.fields.avatarImageURL || ''}
              size="sm"
              fallbackColor={selectedCompany?.fields.fallbackColor}
              alt={selectedCompany?.fields.name || ''}
              variant="rounded"
              text={selectedCompany.fields.name.slice(0, 1)}
            />
            <Body className="text-white truncate" size="sm">
              {selectedCompany?.fields.name}
            </Body>
            <Icon
              icon={open ? 'ChevronUp' : 'ChevronDown'}
              className={'w-[12px] h-[12px] ml-[6px] text-white'}
            />
          </>
        )}
      </Button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        classes={{
          paper: 'w-[240px] mt-[2px] ml-[-8px] shadow-dropdown-menu',
        }}
      >
        <MenuList className="p-0">
          {companies.map((company) => (
            <MenuItem
              key={company.id}
              onClick={handleClick(company)}
              className="h-8 p-3 flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <Avatar
                  src={company.fields.avatarImageURL || ''}
                  size="sm"
                  fallbackColor={company.fields.fallbackColor}
                  alt={company.fields.name}
                  variant="rounded"
                  text={company.fields.name.slice(0, 1)}
                />
                <Heading className="text-[14px]">{company.fields.name}</Heading>
              </div>
              {getCompanyUnreadNotificationsCount(company.id) > 0 ? (
                <NotificationBadge
                  showZero={false}
                  isSidebar
                  content={getCompanyUnreadNotificationsCount(company.id)}
                  backgroundColor={backgroundColor}
                  textColor={textColor}
                />
              ) : null}
            </MenuItem>
          ))}
          <div className="h-[1px] bg-secondary-hover" />
          <MenuItem
            className="h-8 p-3"
            onClick={() => handleLogout(false, signoutRedirectURL, true)}
          >
            <Body className="text-[14px]">Log out</Body>
          </MenuItem>
        </MenuList>
      </Popover>
    </div>
  );
};
